<template>
  <v-card style="width: 75vw; margin: 32px auto">
    <div class="d-flex align-start" style="padding: 24px; gap: 12px">
      <h2 class="mr-auto">Organization Users</h2>
      <ExportButton
        v-if="isadministrator || issuperadministrator"
        :table-type="'users'"
        :workspaces="workspaceFilterOptions"
        :export-message="'Export List'"
      ></ExportButton>
      <Tooltip :message="tooltipMessage"></Tooltip>
    </div>
    <v-row class="d-flex align-center mx-5 mb-3">
      <v-col cols="8">
        <v-text-field v-model="search" label="Search" hide-details clearable></v-text-field>
      </v-col>
      <v-col cols="3">
        <v-btn color="white" @click="newitem()"><v-icon medium class="py-2 mr-2">mdi-plus</v-icon> New</v-btn>
      </v-col>
    </v-row>
    <v-data-table
      dense
      :search="search"
      :headers="headers"
      :items="tableItems"
      :sort-by.sync="sortOptions.sortBy"
      :sort-desc.sync="sortOptions.sortDesc"
      :options.sync="sortHandler"
      :items-per-page="20"
      :custom-key-sort="Object.assign({}, customSortForOrganization, customSortForWorkspaces)"
      :custom-filter="customFilterForUsers"
      :footer-props="footerProps"
      ref="users_data_table"
      class="elevation-1"
    >
      <template v-slot:item="{ item }">
        <v-tooltip :text="'Last login: ' + formatLastLogin(item.lastLogin)" location="mouse" :open-delay="100">
          <template v-slot:activator="{ props }">
            <tr
              v-bind="props"
              @click="editItem(item)"
              class="table-hover"
              :class="{ 'last-selected-row': idOfLastSelectedRow === item.id }"
            >
              <td class="row-text-no-wrap">
                {{ item.firstname }}
              </td>
              <td class="row-text-no-wrap">
                {{ item.lastname }}
              </td>
              <td class="row-text-no-wrap">
                {{ item.email }}
              </td>
              <td class="row-text-no-wrap">
                <div v-if="item.role === 0" style="padding: 10px 0">
                  <p>
                    {{ mapOrganizationIdToOrganizationObject(item.organizationId) }}
                  </p>
                </div>
                <div v-else-if="item.role === 1" style="display: flex; gap: 10px; padding: 10px 0">
                  <p v-for="(organization, index) in item.ownerOrganizations" :key="index">
                    {{ organization.name }}
                  </p>
                </div>
              </td>
              <td>
                <div
                  v-if="item.role === 0 && !item.isAdministrator"
                  style="display: flex; flex-wrap: wrap; gap: 10px; padding: 10px 0"
                >
                  <p v-for="(workspace, index) in mapWorkspaceIdsToWorkspaceObjects(item.workspaceIds)" :key="index">
                    {{ workspace.name }}
                  </p>
                </div>
                <div
                  v-else-if="item.role === 1 && !item.isAdministrator"
                  style="display: flex; flex-wrap: wrap; gap: 10px; padding: 10px 0"
                >
                  <div v-for="(workspace, index) in this.workspaceFilterOptions" :key="index">
                    <p v-if="item.ownerOrganizations.some((oo) => oo.id === workspace.organizationId)">
                      {{ workspace.name }}
                    </p>
                  </div>
                </div>
                <div v-else style="padding: 10px 0">All</div>
              </td>
              <td>
                <div style="display: flex; justify-content: center; margin-right: 21px">
                  <v-icon color="green-darken-1" hide-details v-if="item.authProvider !== 'Local'"> mdi-check </v-icon>
                </div>
              </td>
              <td>
                <div style="display: flex; justify-content: center; margin-right: 21px">
                  <v-icon color="green-darken-1" hide-details v-if="!item.isCleanupEligible"> mdi-check </v-icon>
                </div>
              </td>
              <td>
                <div style="display: flex; justify-content: center; margin-right: 21px">
                  <v-icon color="green-darken-1" hide-details v-if="item.role === 1"> mdi-check </v-icon>
                </div>
              </td>
              <td>
                <div style="display: flex; justify-content: center; margin-right: 21px">
                  <v-icon color="green-darken-1" hide-details v-if="item.isAdministrator"> mdi-check </v-icon>
                </div>
              </td>

              <td>
                <div style="display: flex; align-items: center; justify-content: center">
                  <v-tooltip
                    :text="
                      item.isSuperAdministrator
                        ? 'Super-admin can\'t be deleted'
                        : item.role === 1
                        ? 'Owner can\'t be deleted'
                        : 'Delete'
                    "
                    location="top"
                    :open-delay="tooltipOpenDelayDataTable"
                  >
                    <template v-slot:activator="{ props }">
                      <div
                        v-bind="props"
                        @click.stop="item.role === 1 || item.isSuperAdministrator ? '' : deleteItem(item)"
                        :class="
                          item.role === 1 || item.isSuperAdministrator
                            ? 'data-table-button-disabled'
                            : 'data-table-button'
                        "
                      >
                        <v-icon
                          :disabled="item.role === 1 || item.isSuperAdministrator"
                          color="grey-darken-1"
                          size="30px"
                          >mdi-delete</v-icon
                        >
                      </div>
                    </template>
                  </v-tooltip>
                </div>
              </td>
            </tr>
          </template>
        </v-tooltip>
      </template>
    </v-data-table>
    <v-dialog v-model="dialog" max-width="800px">
      <v-card>
        <v-card-title>
          <span class="headline"> {{ this.editedItem.id === 0 ? "New" : "Edit" }} - User</span>
        </v-card-title>
        <v-card-text>
          <v-col v-if="editedItem.role === 1" cols="12">
            <v-alert type="error" style="padding: 12px 12px 12px 12px; width: 100%; z-index: 1"
              >Contact support to edit owner user information.</v-alert
            >
          </v-col>
          <v-container>
            <v-form v-model="isFormValid" ref="userform">
              <v-row>
                <v-col>
                  <v-text-field
                    :disabled="editedItem.authProvider !== 'Local' || editedItem.role === 1"
                    required
                    :rules="reqRules"
                    ref="firstName"
                    v-model="editedItem.firstname"
                    label="First name"
                  ></v-text-field>
                </v-col>
                <v-col>
                  <v-text-field
                    :disabled="editedItem.authProvider !== 'Local' || editedItem.role === 1"
                    required
                    :rules="reqRules"
                    v-model="editedItem.lastname"
                    label="Last name"
                  ></v-text-field>
                </v-col>
              </v-row>
              <v-row>
                <v-col>
                  <v-text-field
                    :disabled="editedItem.authProvider !== 'Local' || editedItem.role === 1"
                    :rules="editedItem.authProvider !== 'Local' ? reqRules : emailRules"
                    required
                    validate-on-blur
                    v-model="editedItem.email"
                    label="User name / e-mail"
                  ></v-text-field>
                </v-col>
                <v-col v-if="editedItem.id === 0 && editedItem.authProvider === 'Local'">
                  <!-- <v-text-field v-model="editedItem.lastname" label="Password"></v-text-field> -->
                  <v-text-field
                    v-model="editedItem.password"
                    :append-icon="showpassword ? 'mdi-eye' : 'mdi-eye-off'"
                    :rules="passwordRules"
                    :type="showpassword ? 'text' : 'password'"
                    label="Password"
                    :hint="passwordHint"
                    persistent-hint
                    counter
                    @click:append="showpassword = !showpassword"
                    autocomplete="off"
                  />
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12">
                  <v-select
                    :disabled="editedItem.role === 1 || editedItem.isAdministrator"
                    v-model="editedItem.workspaceIds"
                    :items="
                      workspaceFilterOptions.filter((w) =>
                        editedItem.role === 1
                          ? editedItem.ownerOrganizations.some((oo) => oo.id === w.organizationId)
                          : w.organizationId === editedItem.organizationId
                      )
                    "
                    item-value="id"
                    item-title="name"
                    label="Workspaces"
                    multiple
                    hint="Select workspaces"
                    persistent-hint
                  ></v-select>
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12">
                  <v-checkbox
                    :disabled="editedItem.role === 1"
                    class="d-inline-flex"
                    v-model="editedItem.isAdministrator"
                    @update:model-value="
                      (value) => {
                        value
                          ? (editedItem.workspaceIds = workspaceFilterOptions
                              .filter((w) => w.organizationId === editedItem.organizationId)
                              .map((w) => w.id))
                          : editedItem.workspaceIds;
                      }
                    "
                    label="Administrator"
                    color="green-lighten-1"
                  ></v-checkbox>
                </v-col>
                <v-col v-if="editedItem.role !== 1 && !editedItem.isSuperAdministrator" cols="12">
                  <v-checkbox
                    class="d-inline-flex"
                    v-model="editedItem.isCleanupEligible"
                    :true-value="false"
                    :false-value="true"
                    label="Exclude from automatic deletion(Protected)"
                    color="green-lighten-1"
                  >
                  </v-checkbox>
                </v-col>

                <!-- PDF Reports Section - Only visible for admins and if organization allows it -->
                <template v-if="(isadministrator || issuperadministrator) && organizationAllowsPDFReports">
                                 
                  <v-col cols="12">
                    <v-checkbox
                      class="d-inline-flex"
                      v-model="editedItem.sendReport"
                      label="Send automatic issue statistics report emails"
                      color="green-lighten-1"
                    ></v-checkbox>
                  </v-col>

                  <v-col v-if="editedItem.sendReport" cols="12">
                    <v-select
                      v-model="editedItem.reportTimeInterval"
                      :items="[
                        { TimeInterval: 'Monthly', TimeIntervalId: 0 },
                        { TimeInterval: 'Weekly', TimeIntervalId: 1 },
                      ]"
                      item-value="TimeIntervalId"
                      item-title="TimeInterval"
                      label="Report Frequency"
                      hint="How often should reports be sent"
                      persistent-hint
                    ></v-select>
                  </v-col>

                  <v-col cols="12">
                    <v-checkbox
                      class="d-inline-flex"
                      v-model="editedItem.canGeneratePDFReports"
                      label="Allow user to generate reports from Statistics page"
                      color="green-lighten-1"
                    ></v-checkbox>
                  </v-col>
                </template>
                
              </v-row>
            </v-form>
          </v-container>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn tile outlined class="mb-3" @click="dialog = false"
            ><v-icon small class="py-2 mr-1">mdi-close</v-icon> Cancel</v-btn
          >
          <v-btn tile outlined class="mb-3 mr-3" @click="save()" :disabled="!isFormValid"
            ><v-icon small class="py-2 mr-1">mdi-content-save</v-icon> Save</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import { HTTP } from "@/plugins/backendAPI";
import { mapGetters } from "vuex";
// import { EventBus } from '@/store/event-bus.js'
import Tooltip from "../../components/HelpTooltip.vue";
import ExportButton from "../../components/ExportButton.vue";
import defaultDataTableFilter from "../../helpers/defaultFilterForDataTables";
import { PasswordPolicyHelper } from "../../helpers/passwordPolicyHelper";

const emailRegex = /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/;

// (?=.*\d) ensures at least one digit exists
// (?=.*[^\w\såäöæøÅÄÖÆØ]) ensures at least one special character exists, excluding Nordic letters without duplication.
// .{15,} ensures at least 15 characters of any type
const passWdRegex = /^(?=.*\d)(?=.*[^\w\såäöæøÅÄÖÆØ]).{15,}$/;

export default {
  components: { Tooltip, ExportButton },
  data() {
    return {
      passwordRules: [],
      PasswordPolicyHelper: null,
      passwordPolicy: null,
      passwordHint: "",
      dialog: false,
      enableEscClear: true,
      search: "",
      footerProps: { "items-per-page-options": [20, 50, 100] },
      headers: [
        { title: "First name", key: "firstname", width: 100 },
        { title: "Last name", key: "lastname", width: 100 },
        { title: "E-mail", key: "email", width: 150 },
        { title: "Organization", key: "organizationId", width: 100 },
        { title: "Workspaces", key: "workspaceIds" },
        { title: "SSO", key: "authProvider", align: "center", width: 100 },
        { title: "Protected", key: "isCleanupEligible", align: "center", width: 100 },
        { title: "Owner", key: "role", align: "center", width: 100 },
        { title: "Admin", key: "isAdministrator", align: "center", width: 100 },
        { title: "", key: "action", align: "center", sortable: false, width: 100 },
      ],
      customSortForOrganization: {
        organizationId: (d1, d2) => {
          try {
            if (d1 === null) {
              return 1;
            }
            if (d2 === null) {
              return -1;
            }
            if (d1 < d2) {
              return -1;
            }
            return 1;
          } catch (e) {
            return 1;
          }
        },
      },
      customSortForWorkspaces: {
        workspaceIds: (d1, d2) => {
          try {
            if (d1.length < d2.length) {
              return -1;
            }
            return 1;
          } catch (e) {
            return 1;
          }
        },
      },
      editedItem: {},
      tableItems: [],
      workspaceFilterOptions: this.workspaceitems,
      organizationFilterOptions: this.organizationitems,
      editPassRules: [],
      newPassRules: [
        (v) => !!v || "Field is required",
        (v) => !!passWdRegex.test(v) || "At least 15 characters, 1 digit, 1 special character",
      ],
      reqRules: [(v) => !!v || "Field is required"],
      emailRules: [(v) => !!v || "Field is required", (v) => !!emailRegex.test(v) || "Email is in incorrect format"],
      isFormValid: false,
      showpassword: false,
      newItem: false,
      idOfLastSelectedRow: 0,

      sortHandler: {},
      sortOptions: {
        sortBy: [],
        sortDesc: [],
      },
      tooltipOpenDelayDataTable: 200,
      tooltipMessage: [
        "Users",
        "Add, edit, or remove your administrators here. These are the staff members who will handle active issues. Administrators are later connected to your inventory types to receive reports from the system.",
      ],
    };
  },
  computed: {
    ...mapGetters([
      "setPasswordPolicy",
      "sort",
      "organizationid",
      "organizationitems",
      "workspaceitems",
      "isadministrator",
      "issuperadministrator",
      "loading",
    ]),
    organizationid() {
      return this.$store.getters.organizationid;
    },
    organizationAllowsPDFReports() {
      // Hämta aktuell organisation från organizationitems
      const currentOrg = this.organizationitems.find(
        (org) => org.id === Number(this.organizationid)
      );
      if (currentOrg.isSuperAdminOrganization) {
        const currentUserOrg = this.organizationitems.find(
          (org) => org.id === Number(this.editedItem.organizationId)
        );
        return currentUserOrg?.usePdfReports ?? false;
      }
      return currentOrg?.usePdfReports ?? false;
    },
  },
  watch: {
    sortHandler: {
      deep: true,
      handler(obj) {
        const objSort = { sortBy: obj.sortBy, sortDesc: obj.sortDesc };

        if (JSON.stringify(objSort) !== JSON.stringify(this.sortOptions)) {
          const newObj = this.sort;
          newObj.userSort = objSort;
          this.$store.dispatch("updateSort", newObj);
        }
      },
    },
    organizationId(newValue) {
      this.fetchPasswordPolicy(newValue);
    },
    organizationid(newValue) {
      this.getitems(newValue);
      this.getAllWorkspacesForDataTableFiltering();
      this.getAllOrganizationsForDataTableFiltering();
      this.fetchPasswordPolicy(newValue);
    },
    dialog(val) {
      if (val) this.enableEscClear = false;
      if (!val) {
        setTimeout(() => {
          this.enableEscClear = true;
        }, 100);
      }
    },
  },
  created() {
    this.getitems(this.organizationid);
    this.getAllWorkspacesForDataTableFiltering();
    this.getAllOrganizationsForDataTableFiltering();
    this.sortOptions = this.sort.userSort || this.sortOptions;
  },
  mounted() {
    document.addEventListener("keydown", this.clearSearchEsc);
    const savedOrganizationId = localStorage.getItem("organizationid");
    if (savedOrganizationId) {
      this.organizationId = savedOrganizationId;
      this.fetchPasswordPolicy();
    } else {
      console.error("No organization id found in local storage!");
    }
  },
  beforeDestroy() {
    document.removeEventListener("keydown", this.clearSearchEsc);
  },
  methods: {
    // convertWorkspaceIdsToString(workspaceIds) {
    //   if (workspaceIds === undefined || workspaceIds === null || this.workspaceFilterOptions === undefined) {
    //     return []
    //   }
    //   return this.workspaceFilterOptions.filter(wItem => workspaceIds.find(wId => wId === wItem.id)).map(w => w.name).join("; ")
    // },
    formatLastLogin(lastLogin) {
      if (!lastLogin || new Date(lastLogin).getFullYear() < 1000) {
        return "Never logged in";
      }

      try {
        // Tolka som UTC
        const utcDate = new Date(lastLogin + "Z");

        return utcDate.toLocaleString("sv-SE", {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
          timeZone: "Europe/Stockholm",
        });
      } catch (e) {
        return "Invalid date";
      }
    },
    async fetchPasswordPolicy() {
      const organizationId = this.$store.getters.organizationid;

      if (!organizationId || organizationId === 0) {
        console.error("");
        return;
      }

      try {
        this.passwordPolicyHelper = new PasswordPolicyHelper();
        await this.passwordPolicyHelper.fetchPasswordPolicy({ organizationId });

        this.passwordHint = this.passwordPolicyHelper.updatePasswordHint();
        this.passwordRules = this.passwordPolicyHelper.updatePasswordRules();
      } catch (error) {
        console.error("Error when fetching passwordpolicy:", error);
      }
    },

    validatePassword(password) {
      if (!this.passwordPolicyHelper) {
        return false;
      }
      return this.passwordPolicyHelper.validatePassword(password);
    },
    mapWorkspaceIdsToWorkspaceObjects(workspaceIds) {
      if (workspaceIds === undefined || workspaceIds === null || this.workspaceFilterOptions === undefined) {
        return [];
      }
      return this.workspaceFilterOptions.filter((wItem) => workspaceIds.find((wId) => wId === wItem.id));
    },
    mapOrganizationIdToOrganizationObject(organizationId) {
      if (
        this.organizationFilterOptions === undefined ||
        this.organizationFilterOptions === null ||
        this.organizationFilterOptions.length === 0
      ) {
        return "";
      }
      const organization = this.organizationFilterOptions.find((orgItem) => orgItem.id === organizationId);
      return organization != null ? organization.name : "";
    },
    customFilterForUsers(value, search, item) {
      const resultDefault = defaultDataTableFilter(value, search);
      if (Number(this.organizationid) === 2) {
        const resultUsersByWorkspace = this.filterUsersByWorkspace(value, search, item);
        const resultUsersByOrganization = this.filterUsersByOrganization(value, search, item);
        return resultDefault || resultUsersByWorkspace || resultUsersByOrganization;
      } else {
        const resultUsersByWorkspace = this.filterUsersByWorkspace(value, search, item);
        return resultDefault || resultUsersByWorkspace;
      }
    },
    filterUsersByWorkspace(value, search, item) {
      if (!search || value != item.raw.email || !value) {
        return false;
      }
      search = search.toString().toLowerCase();
      const sortedWorkspaceItemByUserAndSearch = this.workspaceFilterOptions.filter((wItem) =>
        item.raw.workspaceIds.find((wId) => wId === wItem.id)
      );
      if (sortedWorkspaceItemByUserAndSearch !== undefined) {
        return sortedWorkspaceItemByUserAndSearch.some((w) => w.name.toLowerCase().includes(search));
      } else {
        return false;
      }
    },
    filterUsersByOrganization(value, search, item) {
      if (!search || value != item.raw.email || !value) {
        return false;
      }
      search = search.toString().toLowerCase();
      let sortedWorkspaceItemByUserAndSearch;
      if (item.raw.organizationId !== null) {
        sortedWorkspaceItemByUserAndSearch = this.organizationitems.filter(
          (oItem) => oItem.id === item.raw.organizationId
        );
      } else {
        sortedWorkspaceItemByUserAndSearch = this.organizationitems.filter((oItem) =>
          item.raw.ownerOrganizations.find((oo) => oo.id === oItem.id)
        );
      }
      if (sortedWorkspaceItemByUserAndSearch !== undefined) {
        return sortedWorkspaceItemByUserAndSearch.some((w) => w.name.toLowerCase().includes(search));
      } else {
        return false;
      }
    },
    getitems(organizationId) {
      if (organizationId >= 1) {
        this.$store.dispatch("setLoading", true);
        HTTP.get(`/users/get?organization-id=${organizationId}`)
          .then((response) => {
            this.tableItems = response.data.map((user) => ({
              ...user,
              lastLoginFormatted: this.formatLastLogin(user.lastLogin),
            }));
            this.allTableItems = response.data;
          })
          .finally(() => {
            this.$store.dispatch("setLoading", false);
          });
      }
    },
    getAllWorkspacesForDataTableFiltering() {
      this.$store.dispatch("setLoading", true);
      HTTP.get(`/workspace/get/filter-options?organization-id=${this.organizationid}`)
        .then((response) => {
          this.workspaceFilterOptions = response.data;
        })
        .catch(() => {
          this.workspaceFilterOptions = [];
        })
        .finally(() => {
          this.$store.dispatch("setLoading", false);
        });
    },
    getAllOrganizationsForDataTableFiltering() {
      this.$store.dispatch("setLoading", true);
      HTTP.get(`/organization/get/filter-options?organization-id=${this.organizationid}`)
        .then((response) => {
          this.organizationFilterOptions = response.data;
        })
        .catch(() => {
          this.organizationFilterOptions = [];
        })
        .finally(() => {
          this.$store.dispatch("setLoading", false);
        });
    },
    newitem() {
      this.newItem = true;
      this.editedItem = {
        id: 0,
        firstname: null,
        lastname: null,
        email: null,
        password: null,
        organizationId: this.organizationid,
        isCleanupEligible: true,
        role: 0,
        authProvider: "Local",
        canGeneratePDFReports: false,
        sendReport: false,
        reportTimeInterval: 0,
      };
      this.editItem(this.editedItem);
    },
    editItem(item) {  
      this.idOfLastSelectedRow = item.id;
      this.editedItem = Object.assign({}, item);
      this.showpassword = false;
      this.dialog = true;
      setTimeout(() => {
        this.$refs.firstName.focus();
      }, 200);
      if (this.$refs.userform) this.$refs.userform.resetValidation();
    },
    deleteItem(item) {
      var self = this;
      if (item.workspaceIds.length > 0) {
        this.$root.$ConfirmDialog
          .open(
            "Deleting primary user",
            "You are about to delete a primary user for one of your workspaces.<br/><br/> <strong>Are you sure?</strong>",
            { color: "red" }
          )
          .then((response) => {
            if (response) {
              this.$store.dispatch("setLoading", true);
              HTTP.post("/users/delete", { id: item.id })
                .then(() => {
                  self.tableItems.splice(self.tableItems.indexOf(item), 1);
                })
                .catch((e) => {
                  self.$store.dispatch("showError", e.response.data);
                })
                .finally(() => {
                  self.$store.dispatch("setLoading", false);
                });
            }
          });
      } else {
        this.$root.$ConfirmDialog.open("Delete item", "Are you sure?", { color: "red" }).then((response) => {
          if (response) {
            this.$store.dispatch("setLoading", true);
            HTTP.post("/users/delete", { id: item.id })
              .then(() => {
                self.tableItems.splice(self.tableItems.indexOf(item), 1);
              })
              .catch((e) => {
                self.$store.dispatch("showError", e.response.data);
              })
              .finally(() => {
                self.$store.dispatch("setLoading", false);
              });
          }
        });
      }
    },

    save() {
      if (this.editedItem.role === 1 || this.editedItem.isSuperAdministrator) {
        this.editedItem.isCleanupEligible = false;
      }
      if (this.editedItem.isSSOUser) {
        this.editedItem.email = this.editedItem.email.substring(0, this.editedItem.email.length - 6);
      }

      this.$store.dispatch("setLoading", true);

      HTTP.post("/users/save", this.editedItem)
        .then((response) => {
          if (response.data.id > 0) {
            const userIndex = this.tableItems.findIndex((user) => user.id === response.data.id);
            if (userIndex !== -1) {
              this.tableItems.splice(userIndex, 1, response.data);
            } else {
              this.tableItems.push(response.data);
            }
            HTTP.patch(`/cleanup/${response.data.id}/eligible`, this.editedItem.isCleanupEligible, {
              headers: { "Content-Type": "application/json" },
            })
              .then((patchResponse) => {
                if (userIndex !== -1) {
                  this.tableItems[userIndex].isCleanupEligible = patchResponse.data.isCleanupEligible;
                }
                this.$store.dispatch("showSuccess", "User saved successfully");
              })
              .catch((e) => {
                this.$store.dispatch("showError", e.response?.data ?? "Unexpected error during cleanup flag update.");
              });
          } else {
            this.editedItem = response.data;
            this.tableItems.push(this.editedItem);
          }

          this.dialog = false;
        })
        .catch((e) => {
          this.$store.dispatch("showError", "Error when saving:<br/><br/>" + (e.response?.data ?? "Unknown error."));
        })
        .finally(() => {
          this.$store.dispatch("setLoading", false);
        });
    },
  },
  clearSearchEsc(e) {
    if (e.keyCode === 27 && this.enableEscClear) {
      this.search = "";
    }
  },
};
</script>
